import { Avatar, AvatarFallback, Badge, Button, Card, Input, Spinner } from '@fc/ui'
import { useQuery } from '@tanstack/react-query'
import { Filter, UserPlus } from 'lucide-react'
import { listPeople, type Person } from '../lib/api'
import { PageHeader } from '../components/page-header'

function initials(p: Person): string {
  return `${p.firstName.at(0) ?? ''}${p.lastName.at(0) ?? ''}`.toUpperCase() || '·'
}

const statusVariant: Record<string, 'success' | 'neutral' | 'gold'> = {
  member: 'success',
  visitor: 'gold',
  inactive: 'neutral',
}

export function PeopleList() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['people'],
    queryFn: () => listPeople(),
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="People"
        description="Everyone in your church community."
        actions={
          <>
            <Button variant="secondary">
              <Filter />
              Filter
            </Button>
            <Button>
              <UserPlus />
              Add person
            </Button>
          </>
        }
      />

      <div className="flex items-center gap-3">
        <Input placeholder="Search by name or email…" className="max-w-sm" />
      </div>

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading people…
          </div>
        ) : isError ? (
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-danger">Couldn’t load people.</p>
            <p className="text-xs text-dim">
              {error instanceof Error ? error.message : 'Is the API running on :3000?'}
            </p>
          </div>
        ) : !data || data.length === 0 ? (
          <div className="py-20 text-center text-sm text-muted">
            No people yet. Add your first person to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold">Email</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold">Campus</th>
                </tr>
              </thead>
              <tbody>
                {data.map((p) => (
                  <tr
                    key={p.id}
                    className="border-b border-border-soft transition-colors last:border-0 hover:bg-card-hover"
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="size-8">
                          <AvatarFallback>{initials(p)}</AvatarFallback>
                        </Avatar>
                        <span className="font-medium">
                          {p.firstName} {p.lastName}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-muted">{p.email ?? '—'}</td>
                    <td className="px-5 py-3">
                      <Badge variant={statusVariant[p.status] ?? 'neutral'} className="capitalize">
                        {p.status}
                      </Badge>
                    </td>
                    <td className="px-5 py-3 text-muted">{p.campus ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
